import { useEffect, useRef } from 'react'

/**
 * MatrixRain — ambient background animation.
 *
 * Direct port of the marketing-site (`dealcloserautopilot.com`) canvas: columns
 * of cobalt characters fall from the top, revealing full snippets as they go,
 * with the occasional cyan flash of a complete phrase. It's meant to sit behind
 * every page as a subtle brand signal.
 *
 * Intentionally low-intensity so it doesn't compete with real dashboard data:
 *   - Per-column opacity stays in 0.08–0.18
 *   - Dashboard snippets are SaaS-operational, not marketing hype
 *   - Respects `prefers-reduced-motion` — renders a single static frame
 *
 * Uses `position: fixed` so it always covers the viewport regardless of
 * container height or scroll position. Keep real content at a higher
 * stacking level (`z-10`+) so it renders above the canvas.
 */

const SNIPPETS: readonly string[] = [
  // Outreach
  'INVITE ACCEPTED',
  'CONNECTION REQUEST SENT',
  'FIRST DM → DELIVERED',
  'FOLLOW-UP SCHEDULED',
  'REPLY RECEIVED',
  'THREAD ESCALATED',
  // Pipeline
  'NEW LEAD → QUALIFIED',
  'QUALIFIED → MEETING',
  'MEETING → WON',
  'PIPELINE → CLOSED WON',
  'STAGE ADVANCED: contacted → replied',
  // Content
  'POST PUBLISHED',
  'POST SCHEDULED',
  'ENGAGEMENT DETECTED',
  'COMMENT LIKED',
  'CONTENT QUEUE OK',
  // Enrichment
  'ENRICHMENT COMPLETE',
  'ICP SCORE: 87',
  'ICP SCORE: 94',
  'SIGNAL → BUYING INTENT',
  // Wins
  'MEETING BOOKED: 2:30 PM',
  'MEETING BOOKED: 10:00 AM',
  'MEETING CONFIRMED: Tomorrow',
  'REPLY: "Let\'s schedule a call"',
  'REPLY: "Send me the proposal"',
  'REPLY: "We\'re interested"',
  // Metrics
  'REPLY RATE: 42%',
  'CONVERSION: 38%',
  'BOOKED: 12 CALLS THIS WEEK',
  'WARMUP → COMPLETE',
]

const COL_SPACING = 30
const FONT_SIZE = 13
const TRAIL_LENGTH = 15
const FLASH_INTERVAL_MIN = 3000
const FLASH_INTERVAL_MAX = 5000

type Column = {
  x: number
  y: number
  speed: number
  snippet: string
  opacity: number
  height: number
  charsDrawn: number
  trail: Array<{ ch: string; y: number }>
}

type Flash = {
  text: string
  x: number
  y: number
  birth: number
  duration: number
}

function randRange(min: number, max: number): number {
  return min + Math.random() * (max - min)
}

function pickSnippet(): string {
  return SNIPPETS[Math.floor(Math.random() * SNIPPETS.length)]!
}

function createColumn(x: number, randomizeY: boolean, height: number): Column {
  return {
    x,
    y: randomizeY ? -Math.random() * height * 1.5 : -FONT_SIZE,
    speed: randRange(0.3, 0.8),
    snippet: pickSnippet(),
    opacity: randRange(0.08, 0.18),
    height: Math.floor(randRange(10, 30)),
    charsDrawn: 0,
    trail: [],
  }
}

type MatrixRainVariant = 'cobalt' | 'green'

const VARIANT_COLORS: Record<MatrixRainVariant, { rain: string; flash: string }> = {
  cobalt: { rain: '28, 63, 255', flash: '6, 182, 212' },     // dashboard default
  green: { rain: '16, 185, 129', flash: '52, 211, 153' },   // staff super-admin
}

type MatrixRainProps = {
  /** Color palette. 'cobalt' = main app, 'green' = staff super-admin. */
  readonly variant?: MatrixRainVariant
}

export function MatrixRain({ variant = 'cobalt' }: MatrixRainProps = {}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)

  useEffect(() => {
    const colors = VARIANT_COLORS[variant]
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const reducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

    let columns: Column[] = []
    const flashes: Flash[] = []
    let nextFlashTime = 0
    let rafId: number | null = null
    let dpr = Math.max(1, window.devicePixelRatio || 1)

    function resize() {
      if (!canvas || !ctx) return
      // Pinned to viewport so the rain is always visible regardless of
      // the host page's scroll height.
      const width = window.innerWidth
      const height = window.innerHeight
      dpr = Math.max(1, window.devicePixelRatio || 1)
      canvas.width = Math.floor(width * dpr)
      canvas.height = Math.floor(height * dpr)
      canvas.style.width = `${width}px`
      canvas.style.height = `${height}px`
      ctx.setTransform(1, 0, 0, 1, 0, 0)
      ctx.scale(dpr, dpr)

      const numCols = Math.ceil(width / COL_SPACING)
      columns = []
      for (let i = 0; i < numCols; i++) {
        columns.push(createColumn(i * COL_SPACING, true, height))
      }
    }

    function drawReducedMotion() {
      if (!ctx) return
      const w = canvas!.clientWidth
      const h = canvas!.clientHeight
      ctx.clearRect(0, 0, w, h)
      ctx.font = `${FONT_SIZE}px "JetBrains Mono", monospace`
      ctx.textBaseline = 'top'
      for (let i = 0; i < 40; i++) {
        const snippet = pickSnippet()
        const x = Math.random() * w
        const y = Math.random() * h
        ctx.fillStyle = `rgba(${colors.rain}, 0.06)`
        ctx.fillText(snippet, x, y)
      }
    }

    function loop(now: number) {
      if (!ctx || !canvas) return
      const w = canvas.clientWidth
      const h = canvas.clientHeight

      ctx.clearRect(0, 0, w, h)
      ctx.font = `${FONT_SIZE}px "JetBrains Mono", monospace`
      ctx.textBaseline = 'top'

      for (let i = 0; i < columns.length; i++) {
        const col = columns[i]!
        col.y += col.speed
        col.charsDrawn += col.speed / FONT_SIZE

        const charIdx = Math.floor(col.charsDrawn) % col.snippet.length
        const ch = col.snippet[charIdx] ?? ' '

        col.trail.push({ ch, y: col.y })
        if (col.trail.length > TRAIL_LENGTH) col.trail.shift()

        for (let t = 0; t < col.trail.length; t++) {
          const item = col.trail[t]!
          const trailProgress = t / col.trail.length
          const trailAlpha = trailProgress * col.opacity
          ctx.fillStyle = `rgba(${colors.rain}, ${trailAlpha})`
          ctx.fillText(item.ch, col.x, item.y)
        }

        // Leading char is slightly brighter with a soft glow
        ctx.fillStyle = `rgba(${colors.rain}, 0.25)`
        ctx.shadowColor = `rgba(${colors.rain}, 0.3)`
        ctx.shadowBlur = 4
        ctx.fillText(ch, col.x, col.y)
        ctx.shadowBlur = 0

        if (col.y > h + FONT_SIZE || col.charsDrawn > col.height) {
          columns[i] = createColumn(col.x, false, h)
        }
      }

      // Occasional fully-readable cyan flash snippet
      if (now > nextFlashTime) {
        flashes.push({
          text: pickSnippet(),
          x: randRange(50, Math.max(100, w - 300)),
          y: randRange(100, Math.max(150, h - 100)),
          birth: now,
          duration: 2000,
        })
        nextFlashTime = now + randRange(FLASH_INTERVAL_MIN, FLASH_INTERVAL_MAX)
      }

      for (let j = flashes.length - 1; j >= 0; j--) {
        const fs = flashes[j]!
        const elapsed = now - fs.birth
        const progress = elapsed / fs.duration
        if (progress >= 1) {
          flashes.splice(j, 1)
          continue
        }
        let alpha: number
        if (progress < 0.3) alpha = (progress / 0.3) * 0.12
        else if (progress > 0.7) alpha = ((1 - progress) / 0.3) * 0.12
        else alpha = 0.12

        ctx.font = `${FONT_SIZE + 1}px "JetBrains Mono", monospace`
        ctx.fillStyle = `rgba(${colors.flash}, ${alpha})`
        ctx.shadowColor = `rgba(${colors.flash}, ${alpha * 0.5})`
        ctx.shadowBlur = 6
        ctx.fillText(fs.text, fs.x, fs.y)
        ctx.shadowBlur = 0
        ctx.font = `${FONT_SIZE}px "JetBrains Mono", monospace`
      }

      rafId = requestAnimationFrame(loop)
    }

    resize()
    window.addEventListener('resize', resize)

    if (reducedMotion) {
      drawReducedMotion()
    } else {
      nextFlashTime = performance.now() + randRange(FLASH_INTERVAL_MIN, FLASH_INTERVAL_MAX)
      rafId = requestAnimationFrame(loop)
    }

    return () => {
      window.removeEventListener('resize', resize)
      if (rafId !== null) cancelAnimationFrame(rafId)
    }
  }, [variant])

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 z-0 h-full w-full"
    />
  )
}
